import { useId, useState } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { Popover as PopoverPrimitive } from 'radix-ui';
import type { ClienteResponse } from '@triade/contracts';
import { useClientes, useCriarCliente } from '@/api/clientes';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDebounced } from '@/hooks/use-debounced';
import { ApiError } from '@/lib/api';
import type { ClienteResumo } from './tipos';

export interface ClienteComboboxProps {
  id?: string;
  valor: ClienteResumo | null;
  onSelecionar: (c: ClienteResumo | null) => void;
  disabled?: boolean;
}

/** Cliente do orcamento: busca por nome/documento (GET /clientes?q=) e criacao minima (POST /clientes). */
export function ClienteCombobox({ id, valor, onSelecionar, disabled }: ClienteComboboxProps) {
  const [aberto, setAberto] = useState(false);
  const [termo, setTermo] = useState('');
  const [criando, setCriando] = useState(false);
  const busca = useDebounced(termo.trim(), 250);
  const clientes = useClientes({ q: busca || undefined, limit: 20 });
  /** Sem resultado ainda: o vazio so vale depois da resposta. Cache da mesma busca (ex.: antes de um rename) nao pode virar "Nenhum". */
  const buscando = clientes.isPending || (clientes.isFetching === true && (clientes.data ?? []).length === 0);
  const listaId = useId();

  return (
    <>
      <PopoverPrimitive.Root open={aberto} onOpenChange={setAberto}>
        <PopoverPrimitive.Trigger asChild>
          <button
            id={id}
            type="button"
            role="combobox"
            aria-expanded={aberto}
            aria-controls={listaId}
            aria-haspopup="listbox"
            disabled={disabled}
            className="flex h-10 w-full items-center gap-2 rounded-[10px] border border-input bg-card px-3 text-left outline-none focus-visible:border-primary focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-60"
          >
            {valor ? <span className="min-w-0 flex-1 truncate">{valor.nome}</span> : <span className="flex-1 text-ink-disabled">Sem cliente</span>}
            <ChevronDown aria-hidden="true" className="size-3.5 shrink-0 text-ink-muted" />
          </button>
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="start"
            sideOffset={6}
            className="z-50 w-[var(--radix-popover-trigger-width)] min-w-[320px] overflow-hidden rounded-xl border border-border bg-popover shadow-menu"
          >
            <Command shouldFilter={false} label="Buscar cliente">
              <CommandInput placeholder="Nome ou documento" value={termo} onValueChange={setTermo} />
              <CommandList id={listaId} aria-label="Clientes">
                {clientes.error ? (
                  <div role="alert" className="px-3 py-4 text-small text-danger-ink">
                    Não foi possível buscar clientes: {clientes.error.message}
                    {clientes.error.code ? ` (${clientes.error.code})` : ''}
                  </div>
                ) : buscando ? (
                  <div role="status" className="px-3 py-4 text-small text-ink-muted">
                    Buscando…
                  </div>
                ) : (
                  <>
                    <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
                    {valor && (
                      <CommandItem
                        value="__nenhum"
                        onSelect={() => {
                          onSelecionar(null);
                          setAberto(false);
                        }}
                      >
                        <span className="text-ink-muted">Sem cliente</span>
                      </CommandItem>
                    )}
                    {(clientes.data ?? []).map((c) => (
                      <CommandItem
                        key={c.id}
                        value={c.id}
                        onSelect={() => {
                          onSelecionar({ id: c.id, nome: c.nome, documento: c.documento, uf: c.uf, contribuinteIcms: c.contribuinteIcms });
                          setAberto(false);
                        }}
                      >
                        <span className="min-w-0 flex-1 truncate">{c.nome}</span>
                        {c.documento && <span className="tabular text-caption text-ink-muted">{c.documento}</span>}
                      </CommandItem>
                    ))}
                  </>
                )}
              </CommandList>
              <button
                type="button"
                onClick={() => {
                  setAberto(false);
                  setCriando(true);
                }}
                className="flex w-full items-center gap-2 border-t border-border px-3 py-2.5 text-left text-small font-medium text-primary hover:bg-line-soft"
              >
                <Plus aria-hidden="true" className="size-4" />
                Novo cliente{termo.trim() ? ` “${termo.trim()}”` : ''}
              </button>
            </Command>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
      <NovoClienteDialog
        aberto={criando}
        nomeInicial={termo.trim()}
        onFechar={() => setCriando(false)}
        onCriado={(c) => {
          onSelecionar({ id: c.id, nome: c.nome, documento: c.documento, uf: c.uf, contribuinteIcms: c.contribuinteIcms });
          setCriando(false);
        }}
      />
    </>
  );
}

interface NovoClienteDialogProps {
  aberto: boolean;
  nomeInicial: string;
  onFechar: () => void;
  onCriado: (c: ClienteResponse) => void;
}

/** Criacao minima: nome e, opcionalmente, CPF/CNPJ (so digitos). Erros da API aparecem com o code. */
export function NovoClienteDialog({ aberto, nomeInicial, onFechar, onCriado }: NovoClienteDialogProps) {
  return (
    <Dialog open={aberto} onOpenChange={(o) => !o && onFechar()}>
      <DialogContent>{aberto && <Formulario nomeInicial={nomeInicial} onFechar={onFechar} onCriado={onCriado} />}</DialogContent>
    </Dialog>
  );
}

function Formulario({ nomeInicial, onFechar, onCriado }: Omit<NovoClienteDialogProps, 'aberto'>) {
  const criar = useCriarCliente();
  const [nome, setNome] = useState(nomeInicial);
  const [documento, setDocumento] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    const doc = documento.replace(/\D/g, '');
    try {
      onCriado(await criar.mutateAsync({ nome: nome.trim(), ...(doc ? { documento: doc } : {}) }));
    } catch (err) {
      setErro(err instanceof ApiError ? `${err.message}${err.code ? ` (${err.code})` : ''}` : 'falha de comunicação com a API');
    }
  }

  return (
    <form onSubmit={(e) => void enviar(e)} className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle>Novo cliente</DialogTitle>
        <DialogDescription>Cadastro mínimo: nome e, se tiver, CPF ou CNPJ.</DialogDescription>
      </DialogHeader>
      <div>
        <Label htmlFor="cli-nome" className="mb-1.5 block text-caption font-medium text-ink-secondary">
          Nome
        </Label>
        <Input id="cli-nome" value={nome} onChange={(e) => setNome(e.target.value)} autoFocus required maxLength={200} />
      </div>
      <div>
        <Label htmlFor="cli-doc" className="mb-1.5 block text-caption font-medium text-ink-secondary">
          CPF ou CNPJ
        </Label>
        <Input id="cli-doc" value={documento} onChange={(e) => setDocumento(e.target.value)} inputMode="numeric" placeholder="Somente números" />
      </div>
      {erro && (
        <p role="alert" className="text-small text-danger-ink">
          {erro}
        </p>
      )}
      <DialogFooter>
        <button type="button" onClick={onFechar} className="h-9 rounded-lg border border-border bg-card px-3.5 text-small font-medium hover:bg-line-soft">
          Cancelar
        </button>
        <button
          type="submit"
          disabled={criar.isPending || nome.trim() === ''}
          className="h-9 rounded-lg bg-primary px-3.5 text-small font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {criar.isPending ? 'Criando…' : 'Criar cliente'}
        </button>
      </DialogFooter>
    </form>
  );
}
